<div class="settings-container">
  <!-- Header Section -->
  <div class="settings-header mb-4">
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h3 class="mb-2">Reporting Settings</h3>
        <p class="text-muted mb-0">Configure reporting preferences and defaults</p>
      </div>
      <button kendoButton class="cyflare-btn-secondary" (click)="resetToDefaults()">
        🔄 Reset to Defaults
      </button>
    </div>
  </div>

  <div class="row">
    <!-- Sidebar Navigation -->
    <div class="col-lg-3">
      <div class="card settings-nav">
        <div class="list-group list-group-flush">
          @for (section of sections; track section.id) {
            <button
              type="button"
              class="list-group-item list-group-item-action"
              [class.active]="isSectionActive(section.id)"
              (click)="selectSection(section.id)"
            >
              <span class="me-2">{{ section.icon }}</span>
              {{ section.label }}
            </button>
          }
        </div>
      </div>
    </div>

    <!-- Settings Content -->
    <div class="col-lg-9">
      @if (isLoading()) {
        <div class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted mt-3">Loading settings...</p>
        </div>
      } @else {
        <!-- Notifications Section -->
        @if (activeSection() === 'notifications') {
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">Notification Preferences</h5>
            </div>
            <div class="card-body">
              <form [formGroup]="notificationForm">
                <h6 class="mb-3">Email Notifications</h6>

                <div class="mb-3">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <label class="form-label mb-0">Report Completion</label>
                      <div class="form-text">Send email when reports are successfully generated</div>
                    </div>
                    <kendo-switch formControlName="emailOnCompletion"></kendo-switch>
                  </div>
                </div>

                <div class="mb-3">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <label class="form-label mb-0">Report Failure</label>
                      <div class="form-text">Send email when report generation fails</div>
                    </div>
                    <kendo-switch formControlName="emailOnFailure"></kendo-switch>
                  </div>
                </div>

                <div class="mb-3">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <label class="form-label mb-0">Schedule Execution</label>
                      <div class="form-text">Notify when scheduled reports run</div>
                    </div>
                    <kendo-switch formControlName="notifyScheduleExecution"></kendo-switch>
                  </div>
                </div>

                <div class="mb-4">
                  <label class="form-label">Default Email Recipients</label>
                  <input
                    type="text"
                    class="form-control"
                    formControlName="emailRecipients"
                    placeholder="email1@example.com, email2@example.com"
                  />
                  <div class="form-text">Comma-separated email addresses</div>
                </div>

                <hr class="my-4" />

                <h6 class="mb-3">Slack Integration</h6>

                <div class="mb-3">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <label class="form-label mb-0">Enable Slack Notifications</label>
                      <div class="form-text">Post notifications to Slack channel</div>
                    </div>
                    <kendo-switch formControlName="slackNotifications"></kendo-switch>
                  </div>
                </div>

                @if (notificationForm.get('slackNotifications')?.value) {
                  <div class="mb-3">
                    <label class="form-label">Slack Webhook URL</label>
                    <input
                      type="text"
                      class="form-control"
                      formControlName="slackWebhook"
                      placeholder="https://hooks.slack.com/services/..."
                    />
                    <div class="form-text">Incoming webhook URL for your Slack workspace</div>
                  </div>
                }

                <div class="d-flex gap-2 mt-4">
                  <button
                    kendoButton
                    class="cyflare-btn-primary"
                    [disabled]="notificationForm.invalid || isSaving()"
                    (click)="saveNotificationSettings()"
                  >
                    @if (isSaving()) {
                      <span class="spinner-border spinner-border-sm me-2"></span>
                    }
                    Save Settings
                  </button>
                  <button
                    kendoButton
                    class="cyflare-btn-secondary"
                    (click)="testNotifications()"
                  >
                    🔔 Send Test
                  </button>
                </div>
              </form>
            </div>
          </div>
        }

        <!-- Report Defaults Section -->
        @if (activeSection() === 'defaults') {
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">Default Report Settings</h5>
            </div>
            <div class="card-body">
              <form [formGroup]="defaultsForm">
                <h6 class="mb-3">Report Content</h6>

                <div class="mb-3">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <label class="form-label mb-0">Include Executive Summary</label>
                      <div class="form-text">High-level overview section</div>
                    </div>
                    <kendo-switch formControlName="includeExecutiveSummary"></kendo-switch>
                  </div>
                </div>

                <div class="mb-3">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <label class="form-label mb-0">Include Detailed Findings</label>
                      <div class="form-text">Comprehensive technical details</div>
                    </div>
                    <kendo-switch formControlName="includeDetailedFindings"></kendo-switch>
                  </div>
                </div>

                <div class="mb-4">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <label class="form-label mb-0">Include Remediation Steps</label>
                      <div class="form-text">Actionable recommendations</div>
                    </div>
                    <kendo-switch formControlName="includeRemediation"></kendo-switch>
                  </div>
                </div>

                <hr class="my-4" />

                <h6 class="mb-3">Delivery & Scheduling</h6>

                <div class="mb-3">
                  <label class="form-label">Default Delivery Method</label>
                  <select class="form-select" formControlName="defaultDeliveryMethod">
                    <option value="download">Download</option>
                    <option value="email">Email</option>
                    <option value="storage">Cloud Storage</option>
                  </select>
                </div>

                <div class="mb-3">
                  <label class="form-label">Default Date Range</label>
                  <select class="form-select" formControlName="defaultDateRange">
                    <option value="last_7_days">Last 7 Days</option>
                    <option value="last_30_days">Last 30 Days</option>
                    <option value="last_90_days">Last 90 Days</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                <div class="mb-4">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <label class="form-label mb-0">Auto-Generate on Schedule</label>
                      <div class="form-text">Automatically run scheduled reports</div>
                    </div>
                    <kendo-switch formControlName="autoGenerateOnSchedule"></kendo-switch>
                  </div>
                </div>

                <button
                  kendoButton
                  class="cyflare-btn-primary"
                  [disabled]="defaultsForm.invalid || isSaving()"
                  (click)="saveDefaultSettings()"
                >
                  @if (isSaving()) {
                    <span class="spinner-border spinner-border-sm me-2"></span>
                  }
                  Save Settings
                </button>
              </form>
            </div>
          </div>
        }

        <!-- Data Retention Section -->
        @if (activeSection() === 'retention') {
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">Data Retention Policies</h5>
            </div>
            <div class="card-body">
              <form [formGroup]="retentionForm">
                <div class="alert alert-info mb-4">
                  <strong>ℹ️ Note:</strong> Retention policies help manage storage and comply with data governance requirements.
                </div>

                <h6 class="mb-3">Report Retention</h6>

                <div class="mb-3">
                  <label class="form-label">Retain Completed Reports</label>
                  <select class="form-select" formControlName="retainCompletedReports">
                    <option value="30">30 days</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                    <option value="180">180 days</option>
                    <option value="365">1 year</option>
                    <option value="0">Forever</option>
                  </select>
                  <div class="form-text">How long to keep successfully generated reports</div>
                </div>

                <div class="mb-4">
                  <label class="form-label">Retain Failed Reports</label>
                  <select class="form-select" formControlName="retainFailedReports">
                    <option value="7">7 days</option>
                    <option value="14">14 days</option>
                    <option value="30">30 days</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                  </select>
                  <div class="form-text">How long to keep failed report jobs</div>
                </div>

                <hr class="my-4" />

                <h6 class="mb-3">Cleanup Options</h6>

                <div class="mb-3">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <label class="form-label mb-0">Auto-Delete Old Reports</label>
                      <div class="form-text">Automatically remove reports past retention period</div>
                    </div>
                    <kendo-switch formControlName="autoDeleteOldReports"></kendo-switch>
                  </div>
                </div>

                <div class="mb-4">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <label class="form-label mb-0">Archive Before Delete</label>
                      <div class="form-text">Move reports to archive storage before deletion</div>
                    </div>
                    <kendo-switch formControlName="archiveBeforeDelete"></kendo-switch>
                  </div>
                </div>

                <button
                  kendoButton
                  class="cyflare-btn-primary"
                  [disabled]="retentionForm.invalid || isSaving()"
                  (click)="saveRetentionSettings()"
                >
                  @if (isSaving()) {
                    <span class="spinner-border spinner-border-sm me-2"></span>
                  }
                  Save Settings
                </button>
              </form>
            </div>
          </div>
        }

        <!-- Templates Section -->
        @if (activeSection() === 'templates') {
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">Report Templates</h5>
            </div>
            <div class="card-body">
              <p class="text-muted mb-4">
                Manage saved report templates. Templates allow you to quickly generate reports with pre-configured settings.
              </p>

              <div class="alert alert-info">
                <strong>💡 Tip:</strong> You can create and manage templates from the Generate tab.
                Click "Save as Template" after configuring a report to create a new template.
              </div>

              <div class="text-center py-4">
                <button kendoButton class="cyflare-btn-primary" routerLink="../generate">
                  Go to Generate Tab
                </button>
              </div>
            </div>
          </div>
        }
      }
    </div>
  </div>
</div>
